import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { IncomeService } from '@/api/services/IncomeService'
import { IManualIncome } from '@/types'
import { apiErrorMessage } from '@/utils/apiError'
import { ManualPayCycleDecision } from './status'

// Keyed by the customer this tab shows, passed in rather than read from IndexedDB, which every tab
// shares.
export const manualIncomeQueryKey = (userId: string) => ['manual-income', userId]

const REVIEW: Record<ManualPayCycleDecision, (userId: string) => Promise<IManualIncome>> = {
  approve: IncomeService.approveManualIncome,
  reject: IncomeService.rejectManualIncome,
}

// data is null when the customer has not submitted a manual income.
export const useManualPayCycle = (userId: string | undefined) => {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: manualIncomeQueryKey(userId ?? ''),
    queryFn: () => IncomeService.fetchManualIncome(userId!),
    enabled: !!userId,
  })

  const mutation = useMutation<
    IManualIncome,
    unknown,
    { decision: ManualPayCycleDecision; userId: string }
  >({
    mutationFn: ({ decision, userId }) => REVIEW[decision](userId),
    // The reply is the request as stored after the review, so it replaces the cached read rather
    // than triggering another one.
    onSuccess: (income, { userId }) =>
      queryClient.setQueryData(manualIncomeQueryKey(userId), income),
  })
  const { mutateAsync } = mutation

  // Rejects with the gateway's message, so callers can show it as-is.
  const review = useCallback(
    async (decision: ManualPayCycleDecision, reviewUserId: string) => {
      try {
        await mutateAsync({ decision, userId: reviewUserId })
      } catch (error) {
        throw new Error(apiErrorMessage(error))
      }
    },
    [mutateAsync]
  )

  return {
    data: query.data,
    // isPending, not isLoading: the query is disabled until the customer is known (restored from
    // IndexedDB after a reload), and a disabled query reports isLoading false with no data, which
    // would read as "nothing submitted".
    isLoading: query.isPending,
    isError: query.isError,
    review,
    isReviewing: mutation.isPending,
  }
}
